import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Flame } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { api, eur } from "@/lib/api";
import Reveal from "@/components/Reveal";

const SLUGS = ["endurax-elite", "endurax-hydrate", "endurax-recovery"];

function MiniCountdown({ launchDate }) {
  const { t } = useLanguage();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  const diff = Math.max(0, new Date(`${launchDate}T00:00:00`) - now);
  const units = [
    [Math.floor(diff / 86400000), t.cs.days, "days"],
    [Math.floor((diff % 86400000) / 3600000), t.cs.hours, "hours"],
    [Math.floor((diff % 3600000) / 60000), t.cs.minutes, "minutes"],
    [Math.floor((diff % 60000) / 1000), t.cs.seconds, "seconds"],
  ];
  return (
    <div className="flex gap-3" data-testid="endurax-countdown">
      {units.map(([val, label, key]) => (
        <div key={key} data-testid={`endurax-countdown-${key}`} className="flex w-16 flex-col items-center border border-gold/25 bg-white/[0.03] px-2 py-3 sm:w-20 sm:px-3">
          <span className="font-display text-2xl font-light tabular-nums text-gold sm:text-3xl">{String(val).padStart(2, "0")}</span>
          <span className="mt-1 text-[8px] uppercase tracking-[0.2em] text-white/50 sm:text-[9px]">{label}</span>
        </div>
      ))}
    </div>
  );
}

export default function Endurax() {
  const { t, lang } = useLanguage();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [launchDate, setLaunchDate] = useState("2027-01-01");

  useEffect(() => {
    api.products().then((list) => setProducts(SLUGS.map((s) => list.find((p) => p.slug === s)).filter(Boolean))).catch(() => {});
    api.config().then((c) => c.launch_date && setLaunchDate(c.launch_date)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const athletes = t.ex?.athletes || [];
  const ingredients = t.ex?.ingredients || [];

  return (
    <div data-testid="endurax-page" className="bg-navy">
      {/* Hero */}
      <section data-testid="endurax-hero" className="relative flex min-h-[92vh] items-center overflow-hidden noise-bg">
        <video autoPlay muted loop playsInline preload="auto" poster="/videos/endurax-poster.jpg" data-testid="endurax-hero-video" className="absolute inset-0 h-full w-full object-cover opacity-45" src="/videos/endurax.mp4" />
        <div className="absolute inset-0 bg-navy/60" />
        <div className="relative mx-auto w-full max-w-7xl px-6 py-32 lg:px-12">
          <Reveal><p className="label-luxe text-gold">{t.ex?.kicker}</p></Reveal>
          <h1 className="mt-6 font-display text-6xl font-light leading-none tracking-tight text-white sm:text-7xl lg:text-8xl">
            ENDURA<span className="text-gold">X</span>
          </h1>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/65 sm:text-lg">{t.ex?.heroSub}</p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10">
              <MiniCountdown launchDate={launchDate} />
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <a href="#formule" data-testid="endurax-hero-cta" className="btn-gold-luxe mt-10 inline-flex items-center gap-3">
              {t.ex?.cta} <ArrowRight size={14} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Products */}
      <section id="formule" data-testid="endurax-products" className="border-t border-gold/10 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal><p className="label-luxe text-gold">{t.ex?.productsKicker}</p></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 max-w-2xl font-display text-4xl font-light leading-tight text-white sm:text-5xl">{t.ex?.productsTitle}</h2></Reveal>
          <div className="mt-14 grid gap-8 lg:grid-cols-3">
            {products.map((p, i) => {
              const loc = p.i18n?.[lang] || p.i18n?.en || {};
              return (
                <Reveal key={p.slug} delay={i * 0.08} className="h-full">
                  <div data-testid={`endurax-card-${p.slug}`} className="flex h-full flex-col border border-white/10 bg-white/[0.02] transition-colors duration-500 hover:border-gold/50">
                    <Link to={`/product/${p.slug}`} className="img-zoom-frame relative block aspect-square overflow-hidden bg-[#0c0c0e]">
                      <img src={p.image} alt={loc.name} className="h-full w-full object-contain" loading="lazy" />
                      <span className="absolute start-4 top-4 bg-navy px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-gold">{t.prod.soonBadge}</span>
                    </Link>
                    <div className="flex flex-1 flex-col p-6">
                      <p className="font-display text-2xl font-light text-white">{loc.name}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-gold-dark">{loc.tagline}</p>
                      <ul className="mt-5 space-y-2.5">
                        {(loc.benefits || []).map((b) => (
                          <li key={b} className="flex items-start gap-2.5 text-sm font-light text-white/60">
                            <Check size={13} className="mt-0.5 shrink-0 text-gold" /> {b}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-auto flex items-center justify-between gap-3 pt-6">
                        <span className="font-display text-2xl font-light text-gold">{eur(p.price)}</span>
                        <button
                          type="button"
                          data-testid={`endurax-add-${p.slug}`}
                          onClick={() => addItem(p)}
                          className="btn-gold-luxe !px-5 !py-2.5"
                        >
                          {t.cs.cta}
                        </button>
                      </div>
                      <Link to={`/product/${p.slug}`} data-testid={`endurax-details-${p.slug}`} className="mt-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-white/40 transition-colors hover:text-gold">
                        {t.ex?.details} →
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ingredients */}
      <section data-testid="endurax-ingredients" className="border-t border-gold/10 bg-[#0d1531] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal><p className="label-luxe text-gold">{t.ex?.ingredientsKicker}</p></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 font-display text-4xl font-light text-white sm:text-5xl">{t.ex?.ingredientsTitle}</h2></Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {ingredients.map((ing, i) => (
              <div key={ing.name} data-testid={`endurax-ingredient-${i}`} className="bg-[#0d1531] p-8 transition-colors duration-500 hover:bg-white/[0.03]">
                <Flame size={16} className="text-gold" />
                <p className="mt-4 font-display text-xl font-light text-white">{ing.name}</p>
                <p className="mt-2 text-sm font-light leading-relaxed text-white/50">{ing.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Athletes */}
      <section data-testid="endurax-athletes" className="border-t border-gold/10 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal><p className="label-luxe text-gold">{t.ex?.athletesKicker}</p></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 font-display text-4xl font-light text-white sm:text-5xl">{t.ex?.athletesTitle}</h2></Reveal>
          <div className="mt-14 grid gap-8 lg:grid-cols-3">
            {athletes.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.08} className="h-full">
                <figure data-testid={`endurax-athlete-${i}`} className="flex h-full flex-col border border-white/10 bg-white/[0.02] p-8">
                  <blockquote className="font-display text-xl font-light italic leading-relaxed text-white/80">“{a.quote}”</blockquote>
                  <figcaption className="mt-auto pt-6">
                    <p className="text-sm font-semibold uppercase tracking-[0.12em] text-gold">{a.name}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-white/40">{a.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="endurax-final" className="relative overflow-hidden border-t border-gold/10 py-24 sm:py-32">
        <div className="spotlight absolute inset-0" />
        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-6 text-center">
          <Reveal><h2 className="font-display text-4xl font-light leading-tight text-white sm:text-6xl">{t.ex?.finalTitle}</h2></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex justify-center">
              <MiniCountdown launchDate={launchDate} />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-lg text-sm font-light leading-relaxed text-white/55">{t.preorderNote}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link to="/shop?cat=performance" data-testid="endurax-final-cta" className="btn-gold-luxe mt-10 inline-flex items-center gap-3">
              {t.ex?.cta} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
